/**
 * Iter-407 · Admin Home V2 (Dark AI Command Center redesign).
 *
 * Same data, same navigation, same test IDs as the previous light
 * version — the only thing that changed is the visual language:
 *   • Dark gradient background (slate-950 → slate-900)
 *   • Teal + Plum/violet accents (matches JobsV2 + BriefingCommandCenter)
 *   • AI Attention header card with priority summary
 *   • High-contrast tiles with backdrop-blur and hover glow
 *   • Consistent typography, spacing, iconography
 *
 * The legacy home page remains available behind ?classic=1.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import AdminAccountMenu from '../../components/AdminAccountMenu';
import MessagesIconButton from '../../components/MessagesIconButton';
import { AdminDmBadge } from '../../components/AdminDmBadge';
import { AdminClientDmBadge } from '../../components/AdminClientDmBadge';
import { AIInsightsTile } from '../../components/AIInsightsTile';
import CommunityHighlights from '../../components/CommunityHighlights';
import CommunityIntroModal, { useCommunityIntroFlag } from '../../components/CommunityIntroModal';
import { useNoOtherDialogOpen } from '../../hooks/useNoOtherDialogOpen';
import {
  Calendar, Users, ShieldCheck, Briefcase, UserCheck,
  DollarSign, ClipboardList, AlertCircle, Star, Building2, Loader2,
  RefreshCw, ChevronRight, Receipt, Activity, ListChecks, Image as ImageIcon, Merge,
  TrendingUp, Sparkles, Zap, Scale, Clock, Bot, Wallet, PhoneCall,
} from 'lucide-react';

/* --------------------------------------------------------------- helpers -- */

// Teal + Plum design tokens. Each accent maps to matching chip/border/dot classes.
const TONES = {
  teal:    { icon: 'text-teal-300',    iconBg: 'bg-teal-500/15 border-teal-500/30',      label: 'text-teal-300/90',    value: 'text-slate-50',   ring: 'group-hover:border-teal-500/60' },
  plum:    { icon: 'text-fuchsia-300', iconBg: 'bg-fuchsia-500/15 border-fuchsia-500/30',label: 'text-fuchsia-300/90', value: 'text-slate-50',   ring: 'group-hover:border-fuchsia-500/60' },
  violet:  { icon: 'text-violet-300',  iconBg: 'bg-violet-500/15 border-violet-500/30',  label: 'text-violet-300/90',  value: 'text-slate-50',   ring: 'group-hover:border-violet-500/60' },
  cyan:    { icon: 'text-cyan-300',    iconBg: 'bg-cyan-500/15 border-cyan-500/30',      label: 'text-cyan-300/90',    value: 'text-slate-50',   ring: 'group-hover:border-cyan-500/60' },
  amber:   { icon: 'text-amber-300',   iconBg: 'bg-amber-500/15 border-amber-500/30',    label: 'text-amber-300/90',   value: 'text-slate-50',   ring: 'group-hover:border-amber-500/60' },
  rose:    { icon: 'text-rose-300',    iconBg: 'bg-rose-500/15 border-rose-500/30',      label: 'text-rose-300/90',    value: 'text-slate-50',   ring: 'group-hover:border-rose-500/60' },
  emerald: { icon: 'text-emerald-300', iconBg: 'bg-emerald-500/15 border-emerald-500/30',label: 'text-emerald-300/90', value: 'text-slate-50',   ring: 'group-hover:border-emerald-500/60' },
  indigo:  { icon: 'text-indigo-300',  iconBg: 'bg-indigo-500/15 border-indigo-500/30',  label: 'text-indigo-300/90',  value: 'text-slate-50',   ring: 'group-hover:border-indigo-500/60' },
};

const Tile = ({ icon: Icon, label, value, hint, tone = 'teal', onClick, testid, urgent }) => {
  const t = TONES[tone] || TONES.teal;
  return (
    <button
      onClick={onClick}
      className={`group text-left rounded-2xl p-4 transition
        border border-slate-800/60 bg-slate-900/60 backdrop-blur-sm
        hover:bg-slate-900/80 hover:shadow-lg hover:shadow-slate-950/40
        ${urgent ? 'ring-1 ring-rose-500/30 border-rose-500/40' : t.ring}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl border grid place-items-center shrink-0 ${t.iconBg}`}>
          <Icon className={`w-5 h-5 ${t.icon}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] uppercase tracking-wider font-bold ${t.label}`}>{label}</div>
          <div className="mt-0.5">
            <span className={`text-2xl font-extrabold tabular-nums ${t.value}`}>{value ?? '—'}</span>
            {hint && <span className="block text-[11px] text-slate-400 leading-tight mt-0.5">{hint}</span>}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-slate-300 transition" />
      </div>
    </button>
  );
};

const Section = ({ title, subtitle, accent = 'teal', children }) => {
  const barTint = {
    teal: 'from-teal-400 to-teal-600',
    plum: 'from-fuchsia-400 to-fuchsia-600',
    amber: 'from-amber-400 to-amber-600',
    emerald: 'from-emerald-400 to-emerald-600',
    rose: 'from-rose-400 to-rose-600',
    cyan: 'from-cyan-400 to-cyan-600',
  }[accent] || 'from-teal-400 to-teal-600';
  return (
    <section className="space-y-3" data-testid={`section-${title.replace(/\s+/g, '-').toLowerCase()}`}>
      <header className="flex items-center gap-3 px-1">
        <span className={`w-1 h-6 rounded-full bg-gradient-to-b ${barTint}`} />
        <div>
          <h2 className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-200">{title}</h2>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </header>
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3">{children}</div>
    </section>
  );
};

/* ----------------------------------------------------------------- page -- */

const HomeV2 = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [home, setHome] = useState({});
  const [notifications, setNotifications] = useState({});
  const [clockInReviews, setClockInReviews] = useState(0);
  const [clientApprovals, setClientApprovals] = useState({});
  const [revenue, setRevenue] = useState({});
  const [perms, setPerms] = useState({});
  const [eventsAudit, setEventsAudit] = useState({ today: 0, unstaffed: 0, applicants: 0 });
  const [opsQa, setOpsQa] = useState({ pending_reimbursements: 0, pending_timesheets: 0 });
  const [idvStats, setIdvStats] = useState({ pending: 0, resubmitted: 0 });
  const [vaQueueCount, setVaQueueCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [payroll, setPayroll] = useState({ funded_awaiting_batch: 0, batches_pending_approval: 0, talents_not_payout_ready: [] });
  const [challengeCount, setChallengeCount] = useState(0);
  const [correctionCount, setCorrectionCount] = useState(0);
  const [aiInquiryCount, setAiInquiryCount] = useState(0);
  const [aiTalentCount, setAiTalentCount] = useState(0);
  const [profile, setProfile] = useState({ display_name: '', profile_photo_url: '' });

  useEffect(() => {
    api.get('/admin/profile/me')
      .then(r => setProfile({
        display_name: r.data?.display_name || '',
        profile_photo_url: r.data?.profile_photo_url || '',
      }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [h, n, cr, ca, p, sh, qa, idv] = await Promise.allSettled([
        api.get('/home'),
        api.get('/admin/notifications/counts'),
        api.get('/attendance/clock-in-reviews/count'),
        api.get('/admin/client-approvals?status=pending'),
        api.get('/admin/permissions'),
        api.get('/admin/shifts?per_page=500&sort=upcoming'),
        api.get('/operations/dashboard/quick-actions'),
        api.get('/admin/idv/stats'),
      ]);
      if (h.status === 'fulfilled') setHome(h.value.data || {});
      if (n.status === 'fulfilled') setNotifications(n.value.data || {});
      if (cr.status === 'fulfilled') setClockInReviews(cr.value.data?.pending_reviews || 0);
      if (ca.status === 'fulfilled') setClientApprovals(ca.value.data?.counts || {});
      if (p.status === 'fulfilled') setPerms(p.value.data?.permissions || {});
      if (qa.status === 'fulfilled') setOpsQa(qa.value.data?.quick_actions || {});
      if (idv.status === 'fulfilled') setIdvStats({
        pending: idv.value.data?.pending || 0,
        resubmitted: idv.value.data?.resubmitted || 0,
      });
      try {
        const va = await api.get('/admin/va-queue/summary');
        setVaQueueCount(va.data?.pending || 0);
      } catch (_) { /* non-fatal */ }
      try {
        const pr = await api.get('/admin/staff-payments/readiness');
        setPayroll(pr.data || { funded_awaiting_batch: 0, batches_pending_approval: 0, talents_not_payout_ready: [] });
      } catch (_) { /* non-fatal */ }
      try {
        const scr = await api.get('/admin/settlement-challenges?status=open');
        setChallengeCount(scr.data?.summary?.open || 0);
      } catch (_) { /* non-fatal */ }
      try {
        const acr = await api.get('/admin/attendance-corrections?status=pending');
        setCorrectionCount(acr.data?.summary?.pending || 0);
      } catch (_) { /* non-fatal */ }
      try {
        const air = await api.get('/ai-avatars/inquiries?status=new');
        setAiInquiryCount(air.data?.summary?.new || 0);
      } catch (_) { /* non-fatal */ }
      try {
        const ait = await api.get('/admin/ai-talent');
        setAiTalentCount(Array.isArray(ait.data) ? ait.data.filter(a => a.published).length : 0);
      } catch (_) { /* non-fatal */ }
      if (sh.status === 'fulfilled') {
        // Iter-412b — Timezone/date-boundary bug fix.
        //
        // Previously we computed "today" as
        //   new Date().toISOString().slice(0, 10)
        // which returns the UTC calendar date. That caused Atlanta
        // Saturday events to appear under "Events Today" starting at
        // ~8 PM Eastern (when UTC crossed midnight) while it was
        // still Friday in Atlanta.
        //
        // The `/admin/shifts` endpoint now stamps `is_local_today`
        // per row using each shift's own timezone (falling back to
        // its state → default America/New_York). Multi-day shifts
        // also flip is_local_today TRUE for every calendar day they
        // span. Never touch UTC or the browser's clock for this
        // decision again.
        const shifts = sh.value.data?.shifts || [];
        const todayShifts = shifts.filter(s => s.is_local_today === true).length;
        const unstaffed = shifts.filter(s => (s.talent_needed || 0) > (s.staffed_count || s.confirmed_count || 0)).length;
        const applicants = shifts.reduce((n, s) => n + (s.applicant_count || s.applicants_count || 0), 0);
        setEventsAudit({ today: todayShifts, unstaffed, applicants });
      }
      setLoading(false);
    };
    load();
    if (perms?.can_view_financial) {
      api.get('/analytics/revenue/snapshot').then(r => setRevenue(r.data || {})).catch(() => {});
    }
  }, [refreshKey, perms?.can_view_financial]);

  const fmtMoney = (v) => v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const today = useMemo(() => new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }), []);

  const { seen: introSeen } = useCommunityIntroFlag();
  const [showCommunityIntro, setShowCommunityIntro] = useState(false);
  const safeToOpenCommunity = useNoOtherDialogOpen(!introSeen && !showCommunityIntro, 'community-intro-modal');
  useEffect(() => {
    if (introSeen) { setShowCommunityIntro(false); return; }
    if (!safeToOpenCommunity) return;
    const t = setTimeout(() => setShowCommunityIntro(true), 2500);
    return () => clearTimeout(t);
  }, [introSeen, safeToOpenCommunity]);

  // Compute the "AI briefing" priority summary
  const priorities = useMemo(() => {
    const items = [];
    if (eventsAudit.today > 0) items.push({ label: `${eventsAudit.today} events running today`, tone: 'teal' });
    if (eventsAudit.applicants > 0) items.push({ label: `${eventsAudit.applicants} applicants awaiting review`, tone: 'plum' });
    if (eventsAudit.unstaffed > 0) items.push({ label: `${eventsAudit.unstaffed} shifts under-staffed`, tone: 'rose' });
    if (idvStats.pending > 0) items.push({ label: `${idvStats.pending} ID verifications pending`, tone: 'amber' });
    if (clockInReviews > 0) items.push({ label: `${clockInReviews} clock-ins to review`, tone: 'violet' });
    if (payroll.batches_pending_approval > 0) items.push({ label: `${payroll.batches_pending_approval} payout batches to approve`, tone: 'emerald' });
    if ((clientApprovals.pending ?? 0) > 0) items.push({ label: `${clientApprovals.pending} client profile approvals`, tone: 'rose' });
    return items;
  }, [eventsAudit, idvStats, clockInReviews, payroll, clientApprovals]);

  const totalUrgent = priorities.length;

  return (
    <div
      className="relative min-h-screen text-slate-100 pb-20 overflow-x-hidden"
      style={{
        background:
          'radial-gradient(ellipse at 20% 0%, rgba(20,184,166,0.08) 0%, transparent 50%),' +
          'radial-gradient(ellipse at 80% 30%, rgba(217,70,239,0.08) 0%, transparent 50%),' +
          'linear-gradient(to bottom, #020617 0%, #0f172a 40%, #020617 100%)',
      }}
      data-testid="admin-home-v2"
    >
      <CommunityIntroModal open={showCommunityIntro} onClose={() => setShowCommunityIntro(false)} />

      {/* Subtle dot grid backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            'radial-gradient(1px 1px at 20% 25%, rgba(148,163,184,0.12) 1px, transparent 1px),' +
            'radial-gradient(1px 1px at 70% 60%, rgba(148,163,184,0.10) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />

      {/* Hero header */}
      <div className="relative border-b border-slate-800/60 bg-slate-950/40 backdrop-blur-sm px-4 md:px-8 py-5">
        <div className="max-w-7xl mx-auto flex items-start md:items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-teal-300/90 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" />
              iEvntür Command Center
            </div>
            <h1
              className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-50 truncate mt-1"
              data-testid="admin-welcome-header"
            >
              Welcome back{profile.display_name ? `, ${profile.display_name}` : ''}
            </h1>
            <p className="text-sm text-slate-400 mt-0.5">
              {today} ·{' '}
              <button
                onClick={() => navigate('/admin/home?classic=1')}
                className="text-teal-300 hover:text-teal-200 hover:underline font-semibold"
                data-testid="switch-classic"
              >
                Switch to classic home
              </button>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => navigate('/admin/smoke-test')}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-emerald-200 border border-emerald-500/30 bg-emerald-500/10 rounded-xl hover:bg-emerald-500/20 hover:border-emerald-500/50 transition"
              data-testid="smoke-test-btn"
              title="Post-Deploy Smoke Test"
            >
              <Activity className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Smoke Test</span>
            </button>
            <button
              onClick={() => setRefreshKey(k => k + 1)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-200 border border-slate-700/60 bg-slate-800/40 rounded-xl hover:bg-slate-800/70 hover:border-slate-600 transition"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Refresh</span>
            </button>
            <MessagesIconButton targetPath="/admin/messages-center" />
            <AdminDmBadge variant="dark" />
            <AdminClientDmBadge variant="dark" />
            <AdminAccountMenu
              displayName={profile.display_name}
              profilePhotoUrl={profile.profile_photo_url}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="relative p-16 flex items-center justify-center">
          <Loader2 className="w-7 h-7 animate-spin text-teal-400" />
        </div>
      ) : (
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-6 space-y-8">

          {/* AI briefing summary — sits above the tiles like a
              command-center at-a-glance readout. */}
          <div
            className="relative overflow-hidden rounded-2xl border border-slate-800/60 bg-gradient-to-br from-slate-950 via-slate-900/90 to-slate-950 p-4 sm:p-5"
            data-testid="ai-priority-briefing"
          >
            <div className="absolute inset-0 opacity-30 pointer-events-none"
              style={{
                background: 'radial-gradient(circle at 10% 20%, rgba(20,184,166,0.20) 0%, transparent 40%), radial-gradient(circle at 90% 80%, rgba(217,70,239,0.18) 0%, transparent 40%)',
              }}
            />
            <div className="relative flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500/30 to-fuchsia-500/30 border border-teal-500/40 flex items-center justify-center shrink-0">
                <Zap className="w-5 h-5 text-teal-200" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-teal-300 flex items-center gap-2">
                  <Sparkles className="w-3 h-3" /> AI Priority Briefing
                </div>
                <div className="text-slate-100 mt-1 font-semibold">
                  {totalUrgent === 0
                    ? 'You are all caught up. No urgent action items right now.'
                    : (
                      <>
                        <span className="text-teal-300 font-extrabold">{totalUrgent}</span>{' '}
                        {totalUrgent === 1 ? 'item needs' : 'items need'} your attention right now.
                      </>
                    )
                  }
                </div>
                {totalUrgent > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid="ai-priority-chips">
                    {priorities.map((p, i) => {
                      const t = TONES[p.tone] || TONES.teal;
                      return (
                        <span
                          key={i}
                          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${t.iconBg} ${t.icon}`}
                        >
                          {p.label}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section 1 — What Needs My Attention Today */}
          <Section title="What Needs My Attention Today" subtitle="Decisions you should action first." accent="rose">
            <Tile icon={Calendar}    tone="teal"    label="Events Today"
                  value={eventsAudit.today} hint="live now / starting"
                  onClick={() => navigate('/admin/shifts?bucket=today')} testid="tile-events-today"
                  urgent={eventsAudit.today > 0} />
            <Tile icon={Users}       tone="plum"    label="Applicants Awaiting Review"
                  value={eventsAudit.applicants} hint="across all events"
                  onClick={() => navigate('/admin/shifts?attention=applicants')} testid="tile-applicants"
                  urgent={eventsAudit.applicants > 0} />
            <Tile icon={ShieldCheck} tone="amber"   label="ID Verification"
                  value={idvStats.pending}
                  hint={idvStats.resubmitted > 0 ? `${idvStats.resubmitted} resubmitted` : 'review queue'}
                  onClick={() => navigate('/admin/idv-review')}
                  testid="tile-idv-review"
                  urgent={idvStats.pending > 0} />
            <Tile icon={TrendingUp}  tone="cyan"    label="Verification Funnel"
                  value=""
                  hint="drop-off tracking"
                  onClick={() => navigate('/admin/verification-funnel')}
                  testid="tile-verification-funnel"
                  urgent={false} />
            <Tile icon={ListChecks}  tone="violet"  label="Capture Queue"
                  value={vaQueueCount}
                  hint="activations missing data"
                  onClick={() => navigate('/admin/va-queue')}
                  testid="tile-va-queue"
                  urgent={vaQueueCount > 0} />
            <Tile icon={Briefcase}   tone="rose"    label="Jobs Needing Staff"
                  value={eventsAudit.unstaffed} hint="shifts under-filled"
                  onClick={() => navigate('/admin/shifts?attention=unfilled')} testid="tile-unstaffed"
                  urgent={eventsAudit.unstaffed > 0} />
            <Tile icon={UserCheck}   tone="teal"    label="Talent Awaiting Confirmation"
                  value={notifications.pending_custom_requests ?? 0} hint="booking responses"
                  onClick={() => navigate('/admin/booking-requests')} testid="tile-pending-confirmations" />
          </Section>

          {/* Section 2 — Payroll Readiness */}
          <Section title="Payout Readiness" subtitle="What's blocking contractor payouts today." accent="emerald">
            <Tile icon={Receipt}      tone="indigo"  label="Funded — Awaiting Batch"
                  value={payroll.funded_awaiting_batch} hint="invoices ready to pay"
                  onClick={() => navigate('/admin/invoices?funded=1')}
                  testid="tile-funded-awaiting"
                  urgent={payroll.funded_awaiting_batch > 0} />
            <Tile icon={ClipboardList} tone="amber"   label="Batches Awaiting Approval"
                  value={payroll.batches_pending_approval} hint="click to approve"
                  onClick={() => navigate('/admin/staff-payments')}
                  testid="tile-batches-approval"
                  urgent={payroll.batches_pending_approval > 0} />
            <Tile icon={AlertCircle}  tone="rose"    label="Talent Not Payout-Ready"
                  value={(payroll.talents_not_payout_ready || []).length}
                  hint="click to send reminders"
                  onClick={() => navigate('/admin/staff-payments?tab=blockers')}
                  testid="tile-talent-not-ready"
                  urgent={(payroll.talents_not_payout_ready || []).length > 0} />
            <Tile icon={Scale}        tone="amber"   label="Settlement Challenges"
                  value={challengeCount} hint="talent disputes to review"
                  onClick={() => navigate('/admin/settlement-challenges')}
                  testid="tile-settlement-challenges"
                  urgent={challengeCount > 0} />
            <Tile icon={Clock}        tone="rose"    label="Attendance Corrections"
                  value={correctionCount} hint="clock-in fixes to approve"
                  onClick={() => navigate('/admin/attendance-corrections')}
                  testid="tile-attendance-corrections"
                  urgent={correctionCount > 0} />
            <Tile icon={Bot}          tone="violet"  label="AI Talent"
                  value={aiTalentCount} hint="published avatars"
                  onClick={() => navigate('/admin/ai-talent')}
                  testid="tile-ai-avatars"
                  urgent={aiInquiryCount > 0} />
          </Section>

          {/* Section 3 — Business Snapshot */}
          <Section title="Business Snapshot" subtitle="Pulse-check on the marketplace." accent="cyan">
            <Tile icon={Building2}   tone="teal"    label="Active Clients"
                  value={home.platform_metrics?.active_clients ?? 0} hint="approved + onboarded"
                  onClick={() => navigate('/admin/users?filter=client')} testid="tile-active-clients" />
            <Tile icon={Users}       tone="cyan"    label="Active Talent"
                  value={(home.platform_metrics?.active_talent ?? 0).toLocaleString()} hint="approved + available"
                  onClick={() => navigate('/admin/users?filter=talent')} testid="tile-active-talent" />
            <Tile icon={Briefcase}   tone="plum"    label="Open Jobs"
                  value={home.platform_metrics?.open_gigs ?? 0} hint="accepting applicants"
                  onClick={() => navigate('/admin/shifts')} testid="tile-open-jobs" />
            <Tile icon={Calendar}    tone="violet"  label="Event History"
                  value="" hint="find & fix past events · Evie recovery"
                  onClick={() => navigate('/admin/event-history')} testid="tile-event-history" />
            <Tile icon={Wallet}      tone="emerald" label="Staff Payments"
                  value="" hint="who's owed · overdue · settlement dates"
                  onClick={() => navigate('/admin/staff-payments')} testid="tile-staff-payments" />
            <Tile icon={PhoneCall}   tone="teal"    label="Evie Phone"
                  value="" hint="AI receptionist · calls · voicemails · setup"
                  onClick={() => navigate('/admin/evie-calls')} testid="tile-evie-phone" />
            <Tile icon={ClipboardList} tone="violet" label="Booking Defaults"
                  value="" hint="standard duties & contact per client"
                  onClick={() => navigate('/admin/client-booking-defaults')} testid="tile-booking-defaults" />
            <Tile icon={DollarSign}  tone="emerald" label="Revenue This Week"
                  value={perms?.can_view_financial ? fmtMoney(revenue.this_week) : '—'}
                  hint={perms?.can_view_financial ? 'gross billings' : 'no access'}
                  onClick={() => navigate('/admin/revenue')} testid="tile-revenue-week" />
            <Tile icon={DollarSign}  tone="emerald" label="Revenue This Month"
                  value={perms?.can_view_financial ? fmtMoney(revenue.this_month) : '—'}
                  hint={perms?.can_view_financial ? 'gross billings' : 'no access'}
                  onClick={() => navigate('/admin/revenue')} testid="tile-revenue-month" />
          </Section>

          {/* Section 4 — Compliance Queue */}
          <Section title="Compliance Queue" subtitle="Reviews and audits to clear out." accent="amber">
            <Tile icon={ClipboardList} tone="violet"  label="Clock-In Reviews"
                  value={clockInReviews} hint="needs approval"
                  onClick={() => navigate('/admin/clock-in-reviews')} testid="tile-clockin-reviews" />
            <Tile icon={AlertCircle}   tone="rose"    label="Profile Reviews"
                  value={clientApprovals.pending ?? 0} hint="client approvals"
                  onClick={() => navigate('/admin/client-approvals')} testid="tile-profile-reviews" />
            <Tile icon={Star}          tone="amber"   label="Rating Reviews"
                  value={home.moderation_queue?.total ?? 0} hint="posts & photos to review"
                  onClick={() => navigate('/admin/community-moderation')} testid="tile-rating-reviews" />
            <Tile icon={ClipboardList} tone="emerald" label="Timesheet Approvals"
                  value={opsQa.pending_timesheets ?? 0} hint="approve hours & pay"
                  onClick={() => navigate('/admin/operations?tab=timesheets')} testid="tile-timesheet-approvals" />
            <Tile icon={Receipt}       tone="plum"    label="Reimbursements"
                  value={opsQa.pending_reimbursements ?? 0} hint="receipts to review"
                  onClick={() => navigate('/admin/expenses')} testid="tile-reimbursements" />
            <Tile icon={ImageIcon}     tone="rose"    label="Gallery Approvals"
                  value="" hint="promote imported photos"
                  onClick={() => navigate('/admin/gallery-approvals')} testid="tile-gallery-approvals" />
            <Tile icon={Merge}         tone="amber"   label="Merge Review"
                  value="" hint="resolve TH import collisions"
                  onClick={() => navigate('/admin/merge-review')} testid="tile-merge-review" />
          </Section>

          <div className="mt-6"><AIInsightsTile /></div>
          <div className="mt-6"><CommunityHighlights /></div>

        </div>
      )}
    </div>
  );
};

export default HomeV2;
